import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUp, useSession } from '../lib/auth-client.ts';
import { http } from '../lib/http.ts';

export default function LoginPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // ALLOW_SIGNUP is enforced server-side either way — this only decides
  // whether to advertise the "Create account" flow in the UI. Defaults to
  // hidden until the check resolves, so a signup-disabled deployment never
  // flashes the option.
  const [allowSignup, setAllowSignup] = useState(false);

  useEffect(() => {
    http
      .get<{ allowSignup: boolean }>('/api/auth-config')
      .then((res) => setAllowSignup(res.data.allowSignup))
      .catch(() => setAllowSignup(false));
  }, []);

  // Signup disabled but somehow still in signup mode (e.g. the toggle was
  // clicked right before the config check resolved) — the switch-back
  // button is hidden in that state, so force it here instead of stranding
  // the user on a form that will just reject their submission.
  useEffect(() => {
    if (!allowSignup && mode === 'signup') setMode('signin');
  }, [allowSignup, mode]);

  // Already signed in (e.g. followed a stale /login link) — leave instead
  // of showing the form again. Done in an effect, not inline during render:
  // calling navigate() mid-render updates the router while this component is
  // still rendering, which React logs as an error and can leave the page in
  // a blank, half-transitioned state until a manual refresh forces a clean mount.
  useEffect(() => {
    if (!isPending && session) navigate('/', { replace: true });
  }, [isPending, session, navigate]);

  if (!isPending && session) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);

    try {
      const { error: authError } = mode === 'signin'
        ? await signIn.email({ email, password })
        : await signUp.email({ email, password, name });

      if (authError) {
        setError(authError.message || 'Something went wrong.');
        return;
      }
      navigate('/', { replace: true });
    } catch {
      // The auth client normally resolves with { error } even on failure —
      // this only catches what it can't: a raw network/CORS rejection (e.g.
      // this origin isn't in the server's CORS_ORIGIN allowlist), which
      // otherwise leaves the button stuck on "Please wait…" forever with no
      // feedback at all.
      setError('Could not reach the server. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dashboard-root login-page">
      <div className="login-card">
        <img
          src="/emr-logo.svg"
          alt="Imarc Forms"
          className="brand-logo"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <h1>{mode === 'signin' ? 'Sign in' : 'Create your account'}</h1>

        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <label>
              Full name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
          )}
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              minLength={8}
              required
            />
          </label>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        {allowSignup && (
          <button
            type="button"
            className="login-switch"
            onClick={() => {
              setMode(mode === 'signin' ? 'signup' : 'signin');
              setError('');
            }}
          >
            {mode === 'signin' ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
          </button>
        )}
      </div>
    </div>
  );
}
